import '../../../../questionsSearch.scss';
import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  useSearchCastleQuestionsMutation,
  useSearchTeacherQuestionsMutation,
  useSearchByQuestionIdMutation,
  useFilterCastleQuestionsMutation,
  useGetCastleQuestionsPageMutation,
  useGoToCastleQuestionMutation,
  useStandardsSearchMutation,
  useSelectRandomQuestionsMutation,
  useGetAssignmentQuestionsMutation,
  useSearchExamQuestionsMutation,
  useGetPublicAssignmentQuestionsMutation
} from '../../../../../../features/authentication/questionSearchApiSlice';
import HesSpinner from '../../../../../../components/loadingOverlay/hesSpinner';
import { useLocation, useParams } from 'react-router-dom';
import { useQuestionSearchContext } from '../../context';
import { useAssignmentContext } from '../../../../../assignments/context';
import SearchFilter from './SearchFilter';
import DetailsBar from './DetailsBar';
import QuestionsList from './QuestionsList';

const QuestionsResults = forwardRef((props, ref) => {
  const [searchCastleQuestionsCall, { isLoading: isRegularSearching }] = useSearchCastleQuestionsMutation();
  const [searchTeacherQuestionsCall, { isLoading: isTeacherSearching }] = useSearchTeacherQuestionsMutation();
  const [searchByQuestionIdCall, { isLoading: isQuestionIdSearching }] = useSearchByQuestionIdMutation();
  const [searchExamQuestionsCall, { isLoading: isExamSearching }] = useSearchExamQuestionsMutation();
  const [getTeacherAssignmentQuestionsCall, { isLoading: isTeacherAssignmentSearching }] = useGetAssignmentQuestionsMutation();
  const [getPublicAssignmentQuestionsCall, { isLoading: isPublicAssignmentSearching }] = useGetPublicAssignmentQuestionsMutation();
  const [filterCastleQuestionsCall, { isLoading: isFiltering }] = useFilterCastleQuestionsMutation();
  const [getPageCastleQuestionsCall, { isLoading: isChangingPage }] = useGetCastleQuestionsPageMutation();
  const [goToCastleQuestionCall, { isLoading: isGoingToQuestion }] = useGoToCastleQuestionMutation();
  const [searchCastleStandardsCall, { isLoading: isSearchingStandards }] = useStandardsSearchMutation();
  const [selectRandomQuestionsCall, { isLoading: isGettingRandomQuestions }] = useSelectRandomQuestionsMutation();
  const [getAssignmentQuestionsCall] = useGetAssignmentQuestionsMutation();
  const questionSearchContext = useQuestionSearchContext();

  const [isError, setIsError] = useState(false);
  const [questionsList, setQuestionsList] = useState([]);
  const [questionsCount, setQuestionsCount] = useState(0);
  const [topicsFilter, setTopicsFilter] = useState([]);
  const [filterToShow, setFiltersToShow] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionsPerPage, setQuestionsPerPage] = useState(25);

  const [showResults, setShowResults] = useState(false);
  const [keywords, setKeywords] = useState();
  const [currentSearchData, setCurrentSearchData] = useState({});
  const [originalSearchRequest, setOriginalSearchRequest] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const { assignmentId } = useParams();
  const [loadingText, setLoadingText] = useState('Searching...');
  const [dateBasedKey, setDateBasedKey] = useState(new Date().getTime().toString());
  const { setAssignmentAssignedViaDetails } = useAssignmentContext();
  const { state } = useLocation();
  const [originalQuestionsList, setOriginalQuestionsList] = useState([]);
  const [filterChange, setFilterChange] = useState(false);
  var tempTopicFlatTree = [];
  var tempStandardFlatTree = [];

  useImperativeHandle(ref, () => ({
    initializeCastleSearch: InitializeCastleSearch,
    initalizeStandardsSearch: InitalizeStandardsSearch,
    initializeTeacherSearch: InitializeTeacherSearch,
    initializeExamSearch: InitializeExamSearch,
    initializeQuestionIdSearch: InitializeQuestionIdSearch,
    initializeTeacherAssignmentSearch: InitializeTeacherAssignmentSearch,
    initializePublicAssignmentSearch: InitializePublicAssignmentSearch,
    endCastleSearch: EndCastleSearch
  }));

  useEffect(() => {

    if (isRegularSearching || isTeacherSearching || isFiltering || isChangingPage || isGoingToQuestion ||
      isSearchingStandards || isGettingRandomQuestions || isExamSearching || isTeacherAssignmentSearching ||
      isPublicAssignmentSearching || isQuestionIdSearching) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }

    if (isRegularSearching || isTeacherSearching || isChangingPage || isGoingToQuestion || isSearchingStandards ||
      isExamSearching || isTeacherAssignmentSearching || isPublicAssignmentSearching || isQuestionIdSearching) {
      setLoadingText('Searching...');
    } else if (isGettingRandomQuestions) {
      setLoadingText('Loading...');
    } else if (isFiltering) {
      setLoadingText('Applying filters...');
    }

  }, [isRegularSearching, isTeacherSearching, isFiltering, isChangingPage, isGoingToQuestion, isSearchingStandards, isGettingRandomQuestions, isExamSearching, isTeacherAssignmentSearching, isPublicAssignmentSearching, isQuestionIdSearching]);

  useEffect(() => {
    if (state?.assignedViaDetails) setAssignmentAssignedViaDetails({ id: state.assignedViaDetails.id, name: state.assignedViaDetails.name });

    if (assignmentId) {
      getAssignmentQuestionsCall(assignmentId).then((results) => {
        if (!results?.data || !results?.data?.success) return;

        setSelectedQuestions(results.data.questions.map((question) => ({ ...question, isSelected: true, inAssignment: true })));
      });
    }
  }, []);

  const InitializeCastleSearch = (searchRequest) => {

    questionSearchContext.setIsSearching(true);
    setQuestionsList([]);
    setShowResults(true);
    setKeywords(searchRequest.keyword);
    setCurrentSearchData({ ...searchRequest, searchType: 'initialRegular' });
    setOriginalSearchRequest({ ...searchRequest, searchType: 'initialRegular' });
    setQuestionsPerPage(25);
    setFiltersToShow(['standards', 'questionTypes', 'contentArea', 'levels', 'topics', 'translations', 'personalAssignments', 'publicAssignments', 'tags', 'attributes']);
    searchCastleQuestionsCall(searchRequest).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
  };

  const InitializeQuestionIdSearch = (searchRequest) => {

    questionSearchContext.setIsSearching(true);
    setQuestionsList([]);
    setShowResults(true);
    setKeywords(searchRequest.keyword);
    setCurrentSearchData({ ...searchRequest, searchType: 'initialQuestionId' });
    setOriginalSearchRequest({ ...searchRequest, searchType: 'initialQuestionId' });
    setQuestionsPerPage(25);
    setFiltersToShow(['standards', 'questionTypes', 'contentArea', 'levels', 'topics', 'translations', 'personalAssignments', 'publicAssignments', 'tags', 'attributes', 'teacherContentSets']);

    searchByQuestionIdCall(searchRequest).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
  };

  const InitializeTeacherSearch = (searchRequest) => {
    questionSearchContext.setIsSearching(true);
    setQuestionsList([]);
    setShowResults(true);
    setKeywords(searchRequest.keyword);
    setCurrentSearchData({ ...searchRequest, searchType: 'initialTeacher' });
    setOriginalSearchRequest({ ...searchRequest, searchType: 'initialTeacher' });
    setQuestionsPerPage(25);
    setFiltersToShow(['standards', 'questionTypes', 'topics', 'teacherContentSets', 'tags', 'attributes']);

    searchTeacherQuestionsCall(searchRequest).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
  };

  const InitalizeStandardsSearch = (searchRequest) => {
    questionSearchContext.setIsSearching(true);
    setQuestionsList([]);
    setShowResults(true);
    setKeywords(searchRequest.keyword);
    setCurrentSearchData({ ...searchRequest, searchType: 'initialStandard' });
    setOriginalSearchRequest({ ...searchRequest, searchType: 'initialStandard' });
    setQuestionsPerPage(25);
    setFiltersToShow(['standards', 'questionTypes', 'contentArea', 'levels', 'topics', 'translations', 'personalAssignments', 'publicAssignments', 'tags', 'attributes']);

    searchCastleStandardsCall(searchRequest.keyword).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
  };

  const InitializeExamSearch = (searchRequest) => {
    questionSearchContext.setIsSearching(true);
    setQuestionsList([]);
    setShowResults(true);
    setKeywords(searchRequest.keyword);
    setFiltersToShow(['standards', 'questionTypes', 'contentArea', 'levels', 'topics', 'translations', 'publicAssignments', 'tags', 'attributes']);

    setCurrentSearchData({ ...searchRequest, searchType: 'initialExam' });
    setOriginalSearchRequest({ ...searchRequest, searchType: 'initialStandard' });
    setQuestionsPerPage(25);
    searchExamQuestionsCall(searchRequest).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
  };

  const InitializeTeacherAssignmentSearch = (assignmentId) => {
    questionSearchContext.setIsSearching(true);
    setQuestionsList([]);
    setShowResults(true);
    setFiltersToShow(['standards', 'questionTypes', 'contentArea', 'levels', 'topics', 'translations', 'personalAssignments', 'tags', 'attributes']);
    setQuestionsPerPage(25);
    getTeacherAssignmentQuestionsCall(assignmentId).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
  };

  const InitializePublicAssignmentSearch = (assignmentId) => {
    questionSearchContext.setIsSearching(true);
    setQuestionsList([]);
    setShowResults(true);
    setFiltersToShow(['standards', 'questionTypes', 'contentArea', 'levels', 'topics', 'translations', 'publicAssignments', 'tags', 'attributes']);
    setQuestionsPerPage(25);
    getPublicAssignmentQuestionsCall(assignmentId).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
  };

  const EndCastleSearch = () => {
    setShowResults(false);
  };

  const MapSearchResults = (searchResults, isNewSearch) => {
    if (!searchResults?.data || !searchResults?.data?.success) {
      questionSearchContext.setIsSearching(false);
      setIsError(true);

      questionSearchContext.setTopicFilters([]);
      questionSearchContext.setStandardFilters([]);
      questionSearchContext.setCourseFilters([]);
      questionSearchContext.setLevelFilters([]);
      questionSearchContext.setPersonalAssignmentFilters([]);
      questionSearchContext.setPublicAssignmentFilters([]);
      questionSearchContext.setTagFilters([]);
      questionSearchContext.setAttributeFilters([]);
      questionSearchContext.setTeacherContentSetsFilter([]);
      questionSearchContext.setQuestionSourceFilters([]);
      questionSearchContext.setTranslationFilters([]);
      questionSearchContext.setQuestionTypeFilters([]);

      setQuestionsList([]);
      setQuestionsCount(0);

      if (searchResults.data && !searchResults.data.success) {
        setCurrentSearchData(originalSearchRequest);
        handleRetrySearch();
      }
      return;
    }
    let searchData = searchResults.data.questions.map((item) => ({
      ...item,
      isSelected: selectedQuestions.find(q => `${q.questionSource}${q.questionId}` == `${item.questionSource}${item.questionId}`) !== undefined,
      inAssignment: selectedQuestions.find(q => `${q.questionSource}${q.questionId}` == `${item.questionSource}${item.questionId}`)?.inAssignment ?? false
    }));
    setQuestionsList(searchData);
    setOriginalQuestionsList(searchData);
    setIsError(false);
    setQuestionsCount(searchResults.data.questionSearchFilters.totalQuestionCount);

    if (isNewSearch) {
      mapNewSearchFilters(searchResults.data.questionSearchFilters);
    } else {
      updateSearchFilters(searchResults.data.questionSearchFilters);
    }

    var classicCoursesCount = searchResults.data.questionSearchFilters.topicsTree?.filter(f => f.isClassicCourse).length;
    var newCoursesCount = searchResults.data.questionSearchFilters.topicsTree?.filter(f => !f.isClassicCourse).length;
    questionSearchContext.setShowClassicCoursesToggle(classicCoursesCount != 0 && newCoursesCount != 0);

    questionSearchContext.setIsSearching(false);
    setPageCount(searchResults.data.questionSearchFilters.pageCount);
  };

  const flattenTreeFilter = (treeList, treeItem, parentId, filterClassicCourses) => {
    filterClassicCourses = !!filterClassicCourses;
    var itemIsHidden = false;
    if (filterClassicCourses) {
      itemIsHidden = treeItem.parent.questionsCount == 0 || (parentId ? false : questionSearchContext.showClassicCourses ? !treeItem.isClassicCourse : treeItem.isClassicCourse);
    } else {
      itemIsHidden = treeItem.parent.questionsCount == 0;
    }

    var treeItemId = treeItem?.parent?.id ? treeItem.parent.id : treeItem.parent.stringId;
    treeList.push({
      parentId: parentId,
      id: treeItemId,
      name: `${treeItem.parent.name} (${treeItem.parent.questionsCount})`,
      childIds: treeItem.children?.slice().sort((a, b) => (a.parent.name > b.parent.name) ? 1 : ((b.parent.name > a.parent.name) ? -1 : 0)).map(c => c.parent.id ? c.parent.id : c.parent.stringId),
      isChecked: false,
      checkIndeterminate: false,
      isExpanded: false,
      isSelected: false,
      isHidden: itemIsHidden
    });

    if (treeItem.children?.length > 0) {
      treeItem.children.slice().map((childItem) => flattenTreeFilter(treeList, childItem, treeItemId));
    }
  }


  const mapFilterToObject = (filter) => {
    return {
      id: filter.id ? filter.id : filter.stringId,
      name: filter.name,
      questionsCount: filter.questionsCount,
      isChecked: false,
      isApplied: false
    }
  }

  const mapNewSearchFilters = (filterResults) => {
    questionSearchContext.setCourseFilters(filterResults.courses?.map(filter => mapFilterToObject(filter)));
    questionSearchContext.setLevelFilters(filterResults.levels?.map(filter => mapFilterToObject(filter)));
    questionSearchContext.setPersonalAssignmentFilters(filterResults.teacherAssignments?.map(filter => mapFilterToObject(filter)));
    questionSearchContext.setPublicAssignmentFilters(filterResults.publicAssignments?.map(filter => mapFilterToObject(filter)));
    questionSearchContext.setTagFilters(filterResults.tags?.map(filter => mapFilterToObject(filter)));
    questionSearchContext.setAttributeFilters(filterResults.attributes?.map(filter => mapFilterToObject(filter)));
    questionSearchContext.setTeacherContentSetsFilter(filterResults.teacherContentSets?.map(filter => mapFilterToObject(filter)));
    if (filterResults.topicsTree) [...filterResults?.topicsTree]?.sort((a, b) => (a.parent.name > b.parent.name) ? 1 : ((b.parent.name > a.parent.name) ? -1 : 0)).map((item) => flattenTreeFilter(tempTopicFlatTree, item, null, true));

    var showAllFilters = filterResults.topicsTree?.filter(f => f.isClassicCourse).length == 0 || filterResults.topicsTree?.filter(f => !f.isClassicCourse).length == 0;
    if (showAllFilters) tempTopicFlatTree = tempTopicFlatTree.map(t => ({ ...t, isHidden: false }));
    questionSearchContext.setTopicFilters(tempTopicFlatTree);

    if (filterResults.standardsTree) [...filterResults?.standardsTree]?.sort((a, b) => (a.parent.name > b.parent.name) ? 1 : ((b.parent.name > a.parent.name) ? -1 : 0)).map((item) => flattenTreeFilter(tempStandardFlatTree, item, null, false));
    questionSearchContext.setStandardFilters(tempStandardFlatTree);

    questionSearchContext.setQuestionSourceFilters([
      {
        id: 'cQuestionsCheckbox',
        name: 'Castle Questions',
        questionsCount: filterResults.castleCourseCount,
        isChecked: false
      },
      {
        id: 'tQuestionsCheckbox',
        name: 'Teacher Questions',
        questionsCount: filterResults.teacherCount,
        isChecked: false
      }
    ]);
    questionSearchContext.setTranslationFilters([
      {
        id: 'spQuestionsCheckbox',
        name: 'Spanish',
        questionsCount: filterResults.spanishCount,
        isChecked: false
      }
    ]);
    questionSearchContext.setQuestionTypeFilters([
      {
        id: 'crCheckbox',
        name: 'Constructed Response',
        questionsCount: filterResults.crCount,
        isChecked: false
      },
      {
        id: 'fiCheckbox',
        name: 'Fill-In',
        questionsCount: filterResults.fiCount,
        isChecked: false
      },

      {
        id: 'mcCheckbox',
        name: 'Multiple Choice',
        questionsCount: filterResults.mcCount,
        isChecked: false
      }
    ]);
  }

  const updateFilterDetails = (filter, resultFilter) => {
    var exisitingFilter = resultFilter?.find(f => filter.id == (f.id ? f.id : f.stringId));

    return {
      ...filter,
      questionsCount: exisitingFilter?.questionsCount ?? 0,
      isChecked: exisitingFilter ? filter.isChecked : false,
      isApplied: exisitingFilter ? filter.isApplied : false
    }
  }

  const updateFilterTreeDetails = (filter, resultFilter, setFilterApplied) => {
    var existingFilter = resultFilter?.find(f => filter.id == f.id);

    if (existingFilter?.isChecked) setFilterApplied(true);
    return {
      ...filter,
      isChecked: existingFilter?.isChecked ?? false,
      isExpanded: existingFilter?.isExpanded ?? false,
      isSelected: existingFilter?.isSelected ?? false,
      checkIndeterminate: existingFilter?.checkIndeterminate ?? false
    }
  }

  const updateSearchFilters = (filterResults) => {
    questionSearchContext.setCourseFilters(fi => fi.map(filter => updateFilterDetails(filter, filterResults.courses)));
    questionSearchContext.setLevelFilters(fi => fi.map(filter => updateFilterDetails(filter, filterResults.levels)));
    questionSearchContext.setPersonalAssignmentFilters(fi => fi.map(filter => updateFilterDetails(filter, filterResults.teacherAssignments)));
    questionSearchContext.setPublicAssignmentFilters(fi => fi.map(filter => updateFilterDetails(filter, filterResults.publicAssignments)));
    questionSearchContext.setTagFilters(fi => fi.map(filter => updateFilterDetails(filter, filterResults.tags)));
    questionSearchContext.setAttributeFilters(fi => fi.map(filter => updateFilterDetails(filter, filterResults.attributes)));
    questionSearchContext.setTeacherContentSetsFilter(fi => fi.map(filter => updateFilterDetails(filter, filterResults.teacherContentSets)));
    [...filterResults.topicsTree]?.sort((a, b) => (a.parent.name > b.parent.name) ? 1 : ((b.parent.name > a.parent.name) ? -1 : 0)).map((item) => flattenTreeFilter(tempTopicFlatTree, item, null, true));
    questionSearchContext.setTopicFilters(a => {
      const newArray = a.map(ar => { return ar; });
      var showAllFilters = filterResults.topicsTree.filter(f => f.isClassicCourse).length == 0 || filterResults.topicsTree.filter(f => !f.isClassicCourse).length == 0;
      if (showAllFilters) tempTopicFlatTree = tempTopicFlatTree.map(t => ({ ...t, isHidden: false }));
      return tempTopicFlatTree.map(t => updateFilterTreeDetails(t, newArray, questionSearchContext.setTopicFiltersApplied));
    });

    [...filterResults.standardsTree]?.sort((a, b) => (a.parent.name > b.parent.name) ? 1 : ((b.parent.name > a.parent.name) ? -1 : 0)).map((item) => flattenTreeFilter(tempStandardFlatTree, item, null, false));
    questionSearchContext.setStandardFilters(a => {
      const newArray = a.map(ar => { return ar; });
      return tempStandardFlatTree.map(t => updateFilterTreeDetails(t, newArray, questionSearchContext.setStandardFiltersApplied));
    });

    var tempQuestionSourceFilter = [
      {
        id: 'cQuestionsCheckbox',
        questionsCount: filterResults.castleCourseCount,
      },
      {
        id: 'tQuestionsCheckbox',
        questionsCount: filterResults.teacherCount,
      }
    ];
    questionSearchContext.setQuestionSourceFilters(fi => fi.map(filter => updateFilterDetails(filter, tempQuestionSourceFilter)));

    var tempTranslationFilter = [
      {
        id: 'spQuestionsCheckbox',
        questionsCount: filterResults.spanishCount
      }
    ];
    questionSearchContext.setTranslationFilters(fi => fi.map(filter => updateFilterDetails(filter, tempTranslationFilter)));

    var tempQuestionTypeFilter = [
      {
        id: 'crCheckbox',
        questionsCount: filterResults.crCount
      },
      {
        id: 'fiCheckbox',
        questionsCount: filterResults.fiCount
      },

      {
        id: 'mcCheckbox',
        questionsCount: filterResults.mcCount
      }
    ];
    questionSearchContext.setQuestionTypeFilters(fi => fi.map(filter => updateFilterDetails(filter, tempQuestionTypeFilter)));
  }


  const handleRetrySearch = () => {
    switch (currentSearchData.searchType) {
      case 'initialRegular':
        searchCastleQuestionsCall(currentSearchData).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
        break;
      case 'initialTeacher':
        searchTeacherQuestionsCall(currentSearchData).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
        break;
      case 'initialStandard':
        searchCastleStandardsCall(currentSearchData.keyword).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
        break;
      case 'initialExam':
        searchExamQuestionsCall(currentSearchData).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
        break;
      case 'filter':
        filterCastleQuestionsCall(currentSearchData).then((results) => { MapSearchResults(results); setSelectedPage(1); });
        break;
      case 'initialQuestionId':
        InitializeQuestionIdSearch(currentSearchData).then((results) => { MapSearchResults(results, true); setSelectedPage(1); });
        break;
    }
  };

  const handleAddQuestions = async () => {
    if (!selectedQuestions || selectedQuestions.length == 0) {
      CustomToast('Please select questions to add', 'error');
      return;
    }
    if (selectedQuestions.length > 100) {
      CustomToast('Maximum 100 questions can be added at a time', 'error');
      return;
    }
    if (assignmentId) {
      addQuestionsToExistingAssignment(assignmentId, '/assignments/' + assignmentId + '/edit_assignment', true);
    } else {
      setOpenOptionTileModal(true);
      setSaveComponent(saveComponentAddToExisting);
    }
  };

  const addQuestionsToExistingAssignment = async (existingAssignmentId, navigatePath, markInAssignment, assignedViaDetails) => {
    if (!selectedQuestions || selectedQuestions.length == 0) {
      return;
    }

    const selectedQuestionsInfoList = Object.values(selectedQuestions).map(selectedQuestion => ({
      questionId: selectedQuestion.questionId,
      courseId: selectedQuestion.courseId
    }));

    var options = {
      id: existingAssignmentId,
      selectedQuestions: selectedQuestionsInfoList
    };
    const response = await addQuestions(options);
    if (response.error) {
      CustomToast(response.error.data.error, 'error');
    }
    else {
      CustomToast('Selected Questions are added', 'success');
      setSelectedQuestions([]);
      setOpenOptionTileModal(false);

      questionsList.forEach(question => {
        const foundQuestion = selectedQuestionsInfoList.find(selectedQuestion => selectedQuestion.questionId === question.questionId);
        question.inAssignment = (markInAssignment && (question.inAssignment || foundQuestion)) ? true : false;
        question.isSelected = false;
      });

      if (assignmentAssignedViaDetails?.id == null && assignedViaDetails?.id != null) setAssignmentAssignedViaDetails({ assignedViaDetails });
      if (navigatePath && navigatePath.length > 0) {
        navigate(navigatePath, { state: { assignedStatus: assignmentStatus, mcChoices: mcChoices, assignedViaDetails: assignedViaDetails ?? assignmentAssignedViaDetails } });
      }
    }
  };

  const handleOptionTileClick = (e) => {
    const id = e.currentTarget.id;
    const option = saveOptions.map((item) =>
      item.id === id ? { ...item, isActive: true } : { ...item, isActive: false }
    );
    setSaveOptions(option);

    if (id === 'createNew') {
      setSaveComponent(saveComponentCreate);
    } else {
      setSaveComponent(saveComponentAddToExisting);
    }
  };

  const handleRandomlySelectQuestions = async () => {
    if (!randomQuestionsRef.current?.value) return;
    var response = await selectRandomQuestionsCall({ alreadySelectedQuestions: selectedQuestions.map(q => q.questionSource + q.questionId), questionCount: randomQuestionsRef.current.value });

    if (response.error) CustomToast('Could not get questions', 'error');

    setSelectedQuestions(selectedQuestions.concat(response.data.map((question) => ({ ...question, isSelected: true }))));
  };

  return (
    <>
      {showResults &&
        <div className='position-relative mt-3'>
          {(isSearching) &&
            <div className='loadingOverlay'>
              <div className='loadingText'>
                <HesSpinner height='30%' maxHeight='230px' />
                <span className='ms-2'>{loadingText}</span>
              </div>
            </div>}
          <div className='d-flex flex-row mb-3 position-relative'>

            <SearchFilter
              filtersToShow={filterToShow}
              setCurrentSearchData={setCurrentSearchData}
              MapSearchResults={MapSearchResults}
              setSelectedPage={setSelectedPage}
              questionsPerPage={questionsPerPage}
              keywords={keywords}
              filterCastleQuestionsCall={filterCastleQuestionsCall}
              questionsFound={questionsCount > 0}
              filterChange={filterChange}
              setFilterChange={setFilterChange}
              topicsFilter={topicsFilter}
              setTopicsFilter={setTopicsFilter}
            />

            <QuestionsList
              questionsCount={questionsCount}
              questionsList={questionsList}
              setQuestionsList={setQuestionsList}
              selectedQuestions={selectedQuestions}
              setSelectedQuestions={setSelectedQuestions}
              isError={isError}
              isSearching={isSearching}
              handleRetrySearch={handleRetrySearch}
              questionsPerPage={questionsPerPage}
              isGettingRandomQuestions={isGettingRandomQuestions}
              getPageCastleQuestionsCall={getPageCastleQuestionsCall}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              goToCastleQuestionCall={goToCastleQuestionCall}
              MapSearchResults={MapSearchResults}
              selectRandomQuestionsCall={selectRandomQuestionsCall}
              setDateBasedKey={setDateBasedKey}
              originalQuestionsList={originalQuestionsList}
            />
          </div>

          {!isError && questionsCount > 0 &&
            <DetailsBar
              questionsPerPage={questionsPerPage}
              setQuestionsPerPage={setQuestionsPerPage}
              selectedQuestions={selectedQuestions}
              setSelectedQuestions={setSelectedQuestions}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              pageCount={pageCount}
              getPageCastleQuestionsCall={getPageCastleQuestionsCall}
              MapSearchResults={MapSearchResults}
              questionsList={questionsList}
              setQuestionsList={setQuestionsList}
              dateBasedKey={dateBasedKey}
              setDateBasedKey={setDateBasedKey}
              keywords={keywords}
              setCurrentSearchData={setCurrentSearchData}
              filterCastleQuestionsCall={filterCastleQuestionsCall}
              originalQuestionsList={originalQuestionsList}
              filterChange={filterChange}
              thumbnailType='questionSearch'
            />
          }
        </div>}
    </>
  );
});

QuestionsResults.displayName = 'QuestionsResults';
export default QuestionsResults;